import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CaretDown as ChevronDown, CaretUp as ChevronUp } from '@phosphor-icons/react';
import { displayFeature } from '@/utils/attributeDisplay';

interface FormattedDescriptionProps {
  description: string;
  equipment?: string[];
}

// Extract key info from text for highlights
function extractHighlights(text: string): string[] {
  const highlights: string[] = [];
  const lower = text.toLowerCase();
  
  // Look for key patterns
  if (lower.includes('fara accident') || lower.includes('fără accident') || lower.includes('niciun accident')) {
    highlights.push('Fără accidente');
  }
  if (lower.includes('nerulat') || lower.includes('nefolosit') || lower.includes('nerulata în românia') || lower.includes('nerulat în românia')) {
    highlights.push('Nerulat în România');
  }
  if (lower.includes('euro 6')) {
    highlights.push('Euro 6');
  }
  if (lower.includes('euro 5')) {
    highlights.push('Euro 5');
  }
  if (lower.includes('un singur proprietar') || lower.includes('primul proprietar')) {
    highlights.push('Un singur proprietar');
  }
  if (lower.includes('service la zi') || lower.includes('istoric service')) {
    highlights.push('Istoric service');
  }
  if (lower.includes('km reali')) {
    highlights.push('Kilometri reali');
  }
  if (lower.includes('stare excelent')) {
    highlights.push('Stare excelentă');
  }
  
  return highlights.slice(0, 4); // Max 4 highlights
}

// Clean description text from CSS/code patterns that may come from scraped pages
function cleanDescription(text: string): string {
  // Quick exit: if text is short and clean (no CSS/HTML markers), return as-is
  // This avoids stripping legitimate URLs like "www.axxyauto.ro" which look like CSS selectors
  const hasCssMarkers = /[{}]|__[\w-]+|\bsvg\s+svg\b|\bdiv\s+div\b|\.ooa-|\.n-accordion|content__inner|var\s*\(--/i.test(text);
  if (!hasCssMarkers) {
    return text.trim();
  }

  // First, check if text is mostly garbage (CSS/HTML)
  const cssPatterns = /\.ooa-|\.n-accordion|header__content|svg svg|div div|content__inner/gi;
  const cssMatches = text.match(cssPatterns) || [];
  
  // If we have many CSS patterns, the text is garbage - return empty
  if (cssMatches.length > 3) {
    // Try to extract only the useful parts
    const usefulPatterns = [
      /Combustibil\s*(\w+)/gi,
      /Capacitate\s*cilindrica\s*([\d\s]+)\s*cm/gi,
      /Putere\s*([\d]+)\s*CP/gi,
      /Transmisie\s*(\w+)/gi,
      /Norma\s*de\s*poluare\s*(Euro\s*\d)/gi,
      /Km\s*([\d\s]+)\s*km/gi,
      /Stare\s*(\w+)/gi,
      /Culoare\s*(\w+)/gi,
    ];
    
    // If even useful patterns have garbage around them, just return empty
    return '';
  }
  
  let cleaned = text
    // Remove CSS class definitions and selectors
    .replace(/\.[\w-]+(?:\[[^\]]*\])?(?::[\w-]+)?(?:\([^)]*\))?(?:::?[\w-]+(?:\([^)]*\))?)*[,\s]*\{[^}]*\}/g, '')
    .replace(/\.[\w-]+(?:\[[^\]]*\])?(?::[\w-]+)?(?:\([^)]*\))?(?:::?[\w-]+(?:\([^)]*\))?)*/g, ' ')
    // Remove svg svg svg patterns
    .replace(/\bsvg\s+svg\s+svg\b/gi, '')
    .replace(/\bsvg\b/gi, '')
    // Remove div div patterns
    .replace(/\bdiv\s+div\s+div\b/gi, '')
    .replace(/\bdiv\b/gi, ' ')
    // Remove CSS property patterns
    .replace(/[a-z-]+:\s*[^;]+;/gi, '')
    .replace(/\{[^}]*\}/g, '')
    // Remove header__content and similar patterns
    .replace(/[\w-]+__[\w-]+/g, ' ')
    // Remove n-accordion patterns
    .replace(/n-accordion[\w-]*/gi, ' ')
    // Remove content_inner patterns
    .replace(/content[_-]inner/gi, ' ')
    // Remove leftover CSS keywords
    .replace(/var\s*\(\s*--[^)]+\)/g, '')
    .replace(/rgba?\s*\([^)]+\)/g, '')
    .replace(/#[a-fA-F0-9]{3,8}\b/g, '')
    // Remove braces
    .replace(/[{}]/g, ' ')
    // Add space before capital letters that follow lowercase (CamelCase splitting)
    .replace(/([a-zăâîșț])([A-ZĂÂÎȘȚ])/g, '$1 $2')
    // Add space before numbers that follow letters
    .replace(/([a-zA-ZăâîșțĂÂÎȘȚ])(\d)/g, '$1 $2')
    // Add space after numbers that precede letters
    .replace(/(\d)([a-zA-ZăâîșțĂÂÎȘȚ])/g, '$1 $2')
    // Clean up Autovit specific junk
    .replace(/Informații de bazăMarca.*?(?=\n|$)/gi, '')
    .replace(/DetaliiInformații de bază.*?(?=\n|$)/gi, '')
    .replace(/Specificații tehnice/gi, '')
    .replace(/Vezi mai mult/gi, '')
    .replace(/Afișează mai puțin/gi, '')
    // Clean up multiple spaces
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/gm, '')
    .trim();
  
  // If after cleaning we have mostly garbage (short text with special chars), return empty
  const alphaNumCount = (cleaned.match(/[a-zA-Z0-9ăâîșțĂÂÎȘȚ]/g) || []).length;
  const totalLength = cleaned.length;
  
  // If less than 60% alphanumeric or too short, it's probably garbage
  if (totalLength > 0 && (alphaNumCount / totalLength < 0.6 || totalLength < 20)) {
    return '';
  }
  
  return cleaned;
}

export function FormattedDescription({ description, equipment }: FormattedDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEquipmentExpanded, setIsEquipmentExpanded] = useState(false);
  
  const { cleanedText, highlights } = useMemo(() => {
    const cleaned = cleanDescription(description);
    const foundHighlights = extractHighlights(description);
    return { cleanedText: cleaned, highlights: foundHighlights };
  }, [description]);
  
  const isLongDescription = cleanedText.length > 500;
  const displayText = isExpanded || !isLongDescription 
    ? cleanedText 
    : cleanedText.slice(0, 500) + '...';

  const normalizedEquipment = useMemo(() => {
    const items = Array.isArray(equipment) ? equipment : [];
    // Dedupe + remove empties
    return Array.from(new Set(items.map((x) => String(x || '').trim()).filter(Boolean)));
  }, [equipment]);

  const EQUIPMENT_PREVIEW_COUNT = 12;
  const equipmentIsLong = normalizedEquipment.length > EQUIPMENT_PREVIEW_COUNT;
  const equipmentToShow = isEquipmentExpanded
    ? normalizedEquipment
    : normalizedEquipment.slice(0, EQUIPMENT_PREVIEW_COUNT);
  
  return (
    <div className="space-y-4">
      {/* Highlights badges - only if we found real highlights */}
      {highlights.length > 0 && (
        <div>
          <p className="mb-2 text-xs text-muted-foreground">Declarat de vânzător</p>
          <div className="flex flex-wrap gap-2">
          {highlights.map((highlight, i) => (
            <Badge 
              key={i} 
              variant="outline"
              className="bg-primary/5 text-primary border-primary/20"
            >
              {highlight}
            </Badge>
          ))}
          </div>
        </div>
      )}
      
      {/* Description text */}
      {displayText && (
        <div className="font-sans text-[15px] md:text-base text-foreground/85 leading-[1.7] tracking-[0.005em] whitespace-pre-wrap antialiased">
          {displayText}
        </div>
      )}
      
      {/* Expand/Collapse button for long descriptions */}
      {isLongDescription && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary hover:text-primary/80 p-0 h-auto"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Arată mai puțin
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Citește mai mult
            </>
          )}
        </Button>
      )}

      {/* Equipment / Dotări (for dealer-imported listings where the full text is stored in attributes.features) */}
      {normalizedEquipment.length > 0 && (
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-sm font-semibold">Dotări</div>
            {equipmentIsLong && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEquipmentExpanded(!isEquipmentExpanded)}
                className="text-primary hover:text-primary/80 p-0 h-auto"
              >
                {isEquipmentExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Arată mai puțin
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Arată toate ({normalizedEquipment.length})
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {equipmentToShow.map((item, i) => (
              <Badge key={`${item}-${i}`} variant="secondary" className="capitalize">
                {displayFeature(item, 'ro')}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
